import { useMemo, useState } from "react";
import { checkEmailRegEx } from "utils/regEx";
import InformationPresenter from "./InformationPresenter";
import useStatusMutation from "hook/useStatusMutation";
import { useAppSelector } from "redux/hooks";
import { isPhoneNumberValid, isUrlValid } from "utils/validHelper";
import {
  getInformation,
  postContactInfo,
  updateContactInfo,
} from "api/helpAPI";
import { useQuery } from "react-query";
import useApiError from "hook/useApiError";
import {
  ContactInfoItem,
  GetContactInfoResponse,
} from "api/interfaces/helpInterface.interface";
import { CustomError } from "api/interfaces/commonInterface.interface";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import { checkEmpty } from "utils/functions";

export const ContactInfoItemKey: (keyof ContactInfoItem)[] = [
  "sites",
  "emails",
  "phones",
  "customFields",
];

const initialData: ContactInfoItem = {
  sites: [],
  emails: [],
  phones: [],
  customFields: [],
};

export default function InformationContainer() {
  const [step, setStep] = useState<number>(0);
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );
  const {
    handleErrorNotify,
    intlString,
    lang,
    createCustomError,
    notifySuccess,
    notifyError,
  } = useApiError();
  const validateMsg: Record<string, string> = {
    sites: intlString(lang.validateMsg.information.invalidURL),
    phone: intlString(lang.validateMsg.information.invalidPhone),
    email: intlString(lang.validateMsg.profile.email.irregular),
    custom: "",
  };

  const [data, setData] = useState<ContactInfoItem>({ ...initialData });

  const {
    data: contactInfo,
    refetch,
    isFetching,
  } = useQuery(
    ["contactInfo", selectedAccount.accountId],
    () => getInformation(selectedAccount.accountId),
    {
      retry: false,
      refetchOnWindowFocus: false,
      onSuccess: (data: GetContactInfoResponse) => {
        if (!data.result || data.error !== 0) {
          const error: CustomError = createCustomError(
            data,
            intlString(lang.label.contact.get.fail)
          );
          handleErrorNotify(error, lang.label.contact.get.fail);
          return;
        }
        const { id, cpAccountId, ...result } = data.result;
        if (
          id &&
          ContactInfoItemKey.reduce(
            (acc, key) => acc + (result?.[key] || []).length,
            0
          ) > 0
        ) {
          setStep(2);
        }
        let newData: Record<keyof ContactInfoItem, any[]> | {} = {};
        ContactInfoItemKey.forEach((key) => {
          newData = {
            ...newData,
            [key]: (result[key] || []).map((item, idx) => ({
              ...item,
              id: `${key.toLowerCase()}_${Date.now()}_${idx}`,
            })),
          };
        });
        setData(newData as ContactInfoItem);
      },
      onError: (error: CustomError) => {
        handleErrorNotify(error, lang.label.contact.get.fail);
      },
    }
  );

  const onClickReset = () => {
    if (hasBeenData) {
      refetch();
      setStep(2);
    } else {
      setStep(0);
      setData({ ...initialData });
    }
  };

  const onAddItem = (itemLabel: keyof ContactInfoItem) => {
    setData((prev) => {
      if (!prev) return prev;
      if (ContactInfoItemKey.includes(itemLabel)) {
        return {
          ...prev,
          [itemLabel]: [
            ...(prev[itemLabel] || []),
            { id: `${itemLabel.toLowerCase()}_${Date.now()}_${Math.random()}` },
          ],
        };
      }
      return prev;
    });
  };

  const onChangeValue = (
    itemLabel: keyof ContactInfoItem,
    key: string,
    value: string,
    idx: number
  ) => {
    if ((value || "").length > 256) {
      return;
    }
    setData((prev) => {
      if (ContactInfoItemKey.includes(itemLabel)) {
        return {
          ...prev,
          [itemLabel]: ((prev[itemLabel] as any[]) || []).map((item, index) =>
            index === idx ? { ...item, [key]: value } : item
          ),
        };
      }
      return prev;
    });
  };

  const onDeleteItem = (itemLabel: keyof ContactInfoItem, idx: number) => {
    setData((prev) => {
      if (ContactInfoItemKey.includes(itemLabel)) {
        return {
          ...prev,
          [itemLabel]: ((prev[itemLabel] as any[]) || []).filter(
            (_: any, index: number) => index !== idx
          ),
        };
      }
      return prev;
    });
  };
  const mutationUpdateInformation = useStatusMutation(
    (payload: ContactInfoItem) =>
      contactInfo.result.id
        ? updateContactInfo(selectedAccount.accountId, payload)
        : postContactInfo(selectedAccount.accountId, payload),
    {
      onSuccess: (res) => {
        if (!res.result || res.error !== 0) {
          const error: CustomError = createCustomError(
            res,
            intlString(lang.validateMsg.information.updateError)
          );
          handleErrorNotify(error, lang.validateMsg.information.updateError);
          return;
        }
        notifySuccess(lang.validateMsg.information.updateSuccess);
        refetch();
        setStep(2);
      },
      onError: (error: CustomError) => {
        handleErrorNotify(error, lang.validateMsg.information.updateError);
      },
    }
  );
  const onClickSave = () => {
    //required인 값이 비어있으면 저장 불가
    if (
      data.sites.some((site) => checkEmpty(site.site)) ||
      data.emails.some((email) => checkEmpty(email.email)) ||
      data.phones.some((phone) => checkEmpty(phone.phone)) ||
      data.customFields.some(
        (customField) => checkEmpty(customField.label)
      )
    ) {
      notifyError(lang.validateMsg.information.requiredEmpty);
      return;
    }
    if (
      data.sites.some((site) => !isUrlValid(site.site)) ||
      data.emails.some((email) => !checkEmailRegEx(email.email)) ||
      data.phones.some((phone) => !isPhoneNumberValid(phone.phone))
    ) {
      notifyError(lang.validateMsg.information.invalidValue);
      return;
    }
    if (
      data.sites.length > 10 ||
      data.emails.length > 10 ||
      data.phones.length > 10 ||
      data.customFields.length > 10
    ) {
      notifyError(lang.validateMsg.information.maxLength);
      return;
    }
    if (!mutationUpdateInformation.idle) {
      return;
    }

    const cleanData = {
      sites: data.sites.map(({ id, ...site }) => site),
      emails: data.emails.map(({ id, ...email }) => email),
      phones: data.phones.map(({ id, ...phone }) => phone),
      customFields: data.customFields.map(({ id, ...field }) => field),
    };

    mutationUpdateInformation.mutate(cleanData);
  };
  const hasBeenData: boolean = useMemo(
    () =>
      contactInfo?.result.id &&
      ContactInfoItemKey.reduce(
        (acc, key) => acc + (contactInfo?.result?.[key] || []).length,
        0
      ) > 0,
    [contactInfo]
  );

  return (
    <InformationPresenter
      step={step}
      setStep={setStep}
      hasBeenData={hasBeenData}
      data={data}
      onAddItem={onAddItem}
      onChangeValue={onChangeValue}
      onDeleteItem={onDeleteItem}
      onClickReset={onClickReset}
      onClickSave={onClickSave}
      validateMsg={validateMsg}
      isLoading={isFetching}
    />
  );
}
